import { AreaRefTableNameEnum, ROUTERS, TableTypes } from '@/constant';
import { useGetListData } from '@/hooks';
import { IUpdateHistoryTarget } from '@/pages/workers/wh1-01/wh1-01.config';
import { TGetAutocompleteSuggestionResponse } from '@/services';
import { CommonActions, useAppDispatch } from '@/stores';
import { memo, useCallback } from 'react';

import { isNilOrEmpty } from 'ramda-adjunct';

import { HyperLink } from '..';

interface IProps {
	record: IUpdateHistoryTarget;
	autocompleteSuggestion: TGetAutocompleteSuggestionResponse;
}

const TargetUpdateHistory = (props: IProps) => {
	const dispatch = useAppDispatch();
	const { params } = useGetListData({
		type: TableTypes.UPDATE_HISTORY,
	});
	const handleClick = useCallback((target: IUpdateHistoryTarget) => {
		switch (target.refTableName) {
			case AreaRefTableNameEnum.APPLICANT:
				return { id: target.refTableId, pathname: ROUTERS.AR1_03 };
			case AreaRefTableNameEnum.FRANCHISE:
			case AreaRefTableNameEnum.WORK_CHANGE_PLACE:
			case AreaRefTableNameEnum.UNAUTHORIZED_DEPARTURE:
			case AreaRefTableNameEnum.ASSIGNMENT_FRANCHISE:
				return { id: target.refTableId, pathname: ROUTERS.FR1_03 };
			case AreaRefTableNameEnum.RECOMMENDER:
				return { id: target.refTableId, pathname: ROUTERS.RM1_03 };
			case AreaRefTableNameEnum.LODGING:
				return { id: target.refTableId, pathname: ROUTERS.LM1_03 };
			case AreaRefTableNameEnum.UNIT:
				return { pathname: ROUTERS.FM1_01 };
			default:
				break;
		}
	}, []);

	const reloadData = useCallback(() => {
		dispatch(
			CommonActions.getUpdateHistoryListRequest({
				...params,
			})
		);
		dispatch(CommonActions.getAutocompleteSuggestionRequest());
	}, [params]);

	const getSuggestionKey = (refTableName: string) => {
		switch (refTableName) {
			case 'APPLICANT':
				return 'applicants';
			case 'FRANCHISE':
				return 'franchises';
			case 'LODGING':
				return 'lodgings';
			case 'RECOMMENDER':
				return 'recommenders';
			default:
				return undefined;
		}
	};

	const isTargetInSuggestion = useCallback(
		(target?: IUpdateHistoryTarget) => {
			if (!target) return false;
			if (target.refTableName === AreaRefTableNameEnum.UNIT) return true;
			const key = getSuggestionKey(target.refTableName);
			if (!key || !props.autocompleteSuggestion?.[key]) return false;
			return props.autocompleteSuggestion?.[key]?.some(
				(item) => Number(item.id) === Number(target.refTableId)
			);
		},
		[props.autocompleteSuggestion]
	);

	if (isNilOrEmpty(props.record?.refTableValue)) {
		return (
			<>
				<span>-</span>
			</>
		);
	}

	return (
		<HyperLink
			className={
				isTargetInSuggestion(props.record)
					? 'text-blue-500 underline cursor-pointer'
					: 'text-gray-500 cursor-default no-underline'
			}
			params={
				isTargetInSuggestion(props.record)
					? handleClick(props.record)
					: undefined
			}
			callback={reloadData}
		>
			{props.record?.refTableValue}
		</HyperLink>
	);
};

export default memo(TargetUpdateHistory);
